import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mdiChevronDown, mdiChevronUp, mdiCodeJson, mdiMinus, mdiPencil, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import * as Cd from './coding-detail.styles';
import { ICodingDiffCodeLine, ICodingDiffDisplayLine, ICodingDiffRegion, ICodingDiffResult, ICodingFieldChange } from './coding-detail.types';
import { buildCodingDiffLines } from './coding-detail.utils';

const EMPTY_DIFF: ICodingDiffResult = { segments: [], regions: [], allLines: [] };

const CodingJsonLine: React.FC<{ text: string }> = ({ text }) => {
    const match = text.match(/^(\s*)"([^"]+)":\s*(.*)$/);
    if (!match) {
        return <Cd.CdJsonPlain>{text}</Cd.CdJsonPlain>;
    }

    const [, indent, key, value] = match;
    const isString = /^"/.test(value);

    return (
        <>
            {indent}
            <Cd.CdJsonKey>&quot;{key}&quot;</Cd.CdJsonKey>
            <Cd.CdJsonColon>: </Cd.CdJsonColon>
            {isString ? <Cd.CdJsonString>{value}</Cd.CdJsonString> : <Cd.CdJsonPrimitive>{value}</Cd.CdJsonPrimitive>}
        </>
    );
};

const changeOpLabel = (op: ICodingFieldChange['op']): string => {
    switch (op) {
        case 'added':
            return 'Added';
        case 'changed':
            return 'Changed';
        case 'removed':
            return 'Removed';
        default:
            return op;
    }
};

const changeOpIcon = (op: ICodingFieldChange['op']): string => {
    switch (op) {
        case 'added':
            return mdiPlus;
        case 'changed':
            return mdiPencil;
        case 'removed':
            return mdiMinus;
        default:
            return mdiPlus;
    }
};

const renderDiffSegment = (segment: ICodingDiffDisplayLine, key: string) => {
    if (segment.kind === 'ellipsis') {
        return (
            <Cd.CdDiffEllipsis key={key}>
                <Cd.CdDiffEllipsisDot />
                {segment.label}
            </Cd.CdDiffEllipsis>
        );
    }

    return (
        <Cd.CdDiffLine key={key} $added={segment.added}>
            <Cd.CdDiffMarker $added={segment.added}>{segment.added ? '+' : ''}</Cd.CdDiffMarker>
            <Cd.CdDiffLineNum>{segment.n}</Cd.CdDiffLineNum>
            <Cd.CdDiffCode $added={segment.added}>
                <CodingJsonLine text={segment.text} />
            </Cd.CdDiffCode>
        </Cd.CdDiffLine>
    );
};

export interface IPayloadChangesCardProps {
    afterPayload: unknown;
    beforePayload: unknown;
    embedded?: boolean;
    /** Field-level changes, computed by the caller because its headline summary needs them too. */
    fieldChanges: ICodingFieldChange[];
    /** Shown instead of the change list, with no raw-payload toggle, when the two payloads must not
     *  be compared (a coding still submitted or cancelled; a worker function with a missing side). */
    notComparedMessage?: string;
    /** Shown in the raw-payload view when there is no after payload to render. */
    noAfterPayloadMessage: string;
}

/**
 * The "Changes to Claim" card shared by the coding and worker-function timeline events: a field
 * change list plus an optional line-level before/after payload diff.
 */
export const PayloadChangesCard: React.FunctionComponent<IPayloadChangesCardProps> = ({
    afterPayload,
    beforePayload,
    embedded = true,
    fieldChanges,
    notComparedMessage,
    noAfterPayloadMessage,
}) => {
    const [showRaw, setShowRaw] = useState(false);
    const [showFullPayload, setShowFullPayload] = useState(false);
    const [activeRegionIndex, setActiveRegionIndex] = useState(0);
    const regionRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const diffScrollRef = useRef<HTMLDivElement | null>(null);

    const shouldAnalyzeDiff = !notComparedMessage;
    const diffData = useMemo(
        () => (shouldAnalyzeDiff ? buildCodingDiffLines(beforePayload, afterPayload) : EMPTY_DIFF),
        [shouldAnalyzeDiff, beforePayload, afterPayload],
    );

    const regionCount = diffData.regions.length;
    const hasChangeRegions = regionCount > 0;
    const truncatedPreviewLines = useMemo(
        () => diffData.segments.filter((segment): segment is ICodingDiffCodeLine => segment.kind === 'code'),
        [diffData.segments],
    );
    // With change regions: always render the full payload (scrolled to changes).
    // With no added lines: honor buildCodingDiffLines truncation (first 20) unless expanded.
    const displayLines = hasChangeRegions || showFullPayload ? diffData.allLines : truncatedPreviewLines;
    const canToggleFullPayload = !hasChangeRegions && diffData.allLines.length > truncatedPreviewLines.length;
    const hasMultipleRegions = regionCount > 1;
    const hasRawPayloadContent = diffData.allLines.length > 0;
    const regionsByLineIndex = useMemo(() => {
        const byIndex = new Map<number, ICodingDiffRegion>();
        for (const region of diffData.regions) {
            byIndex.set(region.allLineIndex, region);
        }
        return byIndex;
    }, [diffData.regions]);

    const rawPayloadEmptyMessage =
        afterPayload == null ? noAfterPayloadMessage : 'No line-level differences to display for this payload.';

    const rawPayloadFallbackBody = useMemo(() => {
        if (afterPayload == null) {
            return '{}';
        }

        try {
            return JSON.stringify(afterPayload, null, 2) || '{}';
        } catch {
            return '{}';
        }
    }, [afterPayload]);

    const scrollToRegion = useCallback(
        (regionIndex: number, behavior: ScrollBehavior = 'smooth') => {
            const region = diffData.regions[regionIndex];
            if (!region) {
                return;
            }

            const anchor = regionRefs.current[region.id];
            const container = diffScrollRef.current;
            if (anchor && container) {
                const nextTop =
                    container.scrollTop +
                    (anchor.getBoundingClientRect().top - container.getBoundingClientRect().top) -
                    8;
                container.scrollTo({ top: Math.max(0, nextTop), behavior });
            }
            setActiveRegionIndex(regionIndex);
        },
        [diffData.regions],
    );

    const goToPreviousRegion = useCallback(() => {
        if (!hasMultipleRegions) {
            return;
        }
        scrollToRegion((activeRegionIndex - 1 + regionCount) % regionCount);
    }, [activeRegionIndex, hasMultipleRegions, regionCount, scrollToRegion]);

    const goToNextRegion = useCallback(() => {
        if (!hasMultipleRegions) {
            return;
        }
        scrollToRegion((activeRegionIndex + 1) % regionCount);
    }, [activeRegionIndex, hasMultipleRegions, regionCount, scrollToRegion]);

    useEffect(() => {
        if (!showRaw) {
            setShowFullPayload(false);
            setActiveRegionIndex(0);
            return;
        }

        if (regionCount === 0) {
            return;
        }

        // Jump viewport to the first change region once the full payload is mounted.
        const frameId = requestAnimationFrame(() => {
            scrollToRegion(0, 'auto');
        });
        return () => cancelAnimationFrame(frameId);
    }, [showRaw, regionCount, scrollToRegion]);

    return (
        <Cd.CdSectionCard $embedded={embedded}>
            <Cd.CdChangesHeader>
                <Cd.CdSectionTitle style={{ margin: 0 }}>Changes to Claim</Cd.CdSectionTitle>
                {shouldAnalyzeDiff && (
                    <Cd.CdToggleButton
                        type="button"
                        variant="unstyled"
                        $active={showRaw}
                        onClick={() => setShowRaw(value => !value)}>
                        <Icon path={mdiCodeJson} size={0.62} />
                        {showRaw ? 'Hide raw payload' : 'View raw payload'}
                    </Cd.CdToggleButton>
                )}
            </Cd.CdChangesHeader>

            {notComparedMessage ? (
                <Cd.CdEmptyState>{notComparedMessage}</Cd.CdEmptyState>
            ) : fieldChanges.length > 0 ? (
                <Cd.CdChangesList $scrollable={fieldChanges.length > 5}>
                    {fieldChanges.map(change => (
                        <Cd.CdChangeCard key={change.field} $op={change.op}>
                            <Cd.CdChangeOp $op={change.op}>
                                <Icon path={changeOpIcon(change.op)} size={0.58} />
                                {changeOpLabel(change.op)}
                            </Cd.CdChangeOp>
                            <Cd.CdChangeBody>
                                <Cd.CdChangeLabel>{change.label}</Cd.CdChangeLabel>
                                <Cd.CdChangeField>{change.field}</Cd.CdChangeField>
                            </Cd.CdChangeBody>
                            <Cd.CdChangeValue>{change.value}</Cd.CdChangeValue>
                        </Cd.CdChangeCard>
                    ))}
                </Cd.CdChangesList>
            ) : (
                <Cd.CdEmptyState>No field-level changes detected between before and after payloads.</Cd.CdEmptyState>
            )}

            {shouldAnalyzeDiff && showRaw && (
                <Cd.CdDiffWrap>
                    <Cd.CdDiffHeader>
                        <Cd.CdDiffTitle>Payload diff</Cd.CdDiffTitle>
                        <Cd.CdDiffSubtitle>
                            {hasRawPayloadContent
                                ? hasChangeRegions
                                    ? regionCount > 1
                                        ? `full after payload · ${regionCount} change regions`
                                        : 'full after payload · scrolled to change'
                                    : showFullPayload
                                      ? 'full after payload · line numbers'
                                      : `preview · first ${displayLines.length} lines`
                                : 'no diff available'}
                        </Cd.CdDiffSubtitle>
                        {hasRawPayloadContent && hasMultipleRegions && (
                            <Cd.CdDiffNav>
                                <Cd.CdDiffNavButton type="button" variant="unstyled" onClick={goToPreviousRegion}>
                                    <Icon path={mdiChevronUp} size={0.62} />
                                    Prev
                                </Cd.CdDiffNavButton>
                                <Cd.CdDiffNavCount>
                                    Change {activeRegionIndex + 1} of {regionCount}
                                </Cd.CdDiffNavCount>
                                <Cd.CdDiffNavButton type="button" variant="unstyled" onClick={goToNextRegion}>
                                    Next
                                    <Icon path={mdiChevronDown} size={0.62} />
                                </Cd.CdDiffNavButton>
                            </Cd.CdDiffNav>
                        )}
                        {hasRawPayloadContent && (
                            <Cd.CdDiffNav>
                                {canToggleFullPayload && (
                                    <Cd.CdDiffNavButton
                                        type="button"
                                        variant="unstyled"
                                        onClick={() => setShowFullPayload(value => !value)}>
                                        {showFullPayload ? 'Show fewer lines' : 'Show all lines'}
                                    </Cd.CdDiffNavButton>
                                )}
                                <Cd.CdDiffLegend>
                                    <Cd.CdDiffLegendSwatch />
                                    Added
                                </Cd.CdDiffLegend>
                            </Cd.CdDiffNav>
                        )}
                    </Cd.CdDiffHeader>
                    {hasRawPayloadContent ? (
                        <Cd.CdDiffScroll ref={diffScrollRef}>
                            <Cd.CdDiffBody>
                                {displayLines.map((line, index) => {
                                    const region = regionsByLineIndex.get(index);
                                    if (region) {
                                        return (
                                            <Cd.CdDiffRegionAnchor
                                                key={`${line.n}-${line.text}-${index}`}
                                                ref={node => {
                                                    regionRefs.current[region.id] = node;
                                                }}>
                                                {renderDiffSegment(line, `${line.n}-${line.text}-${index}`)}
                                            </Cd.CdDiffRegionAnchor>
                                        );
                                    }

                                    return renderDiffSegment(line, `${line.n}-${line.text}-${index}`);
                                })}
                            </Cd.CdDiffBody>
                        </Cd.CdDiffScroll>
                    ) : (
                        <Cd.CdDiffEmptyPayload>
                            <Cd.CdDiffEmptyPayloadText>{rawPayloadEmptyMessage}</Cd.CdDiffEmptyPayloadText>
                            <Cd.CdDiffEmptyPayloadCode>{rawPayloadFallbackBody}</Cd.CdDiffEmptyPayloadCode>
                        </Cd.CdDiffEmptyPayload>
                    )}
                </Cd.CdDiffWrap>
            )}
        </Cd.CdSectionCard>
    );
};
